/**
 * 检查光标是否在节点的末尾
 *
 * @param el - 节点元素
 * @returns 是否在末尾
 */
export const isCursorAtEnd = (el: HTMLElement): boolean => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return false;

    const range = selection.getRangeAt(0);
    if (!range.collapsed) return false;

    const elementRange = document.createRange();
    elementRange.selectNodeContents(el);
    elementRange.collapse(false);

    // 首先检查是否在同一位置
    const isSamePosition = range.compareBoundaryPoints(Range.START_TO_START, elementRange) === 0;

    // 如果位置不同，还需要检查是否只是视觉上的差异（比如额外的换行符）
    if (!isSamePosition) {
        // 获取从光标到末尾的内容
        const tempRange = range.cloneRange();
        tempRange.setEndAfter(el.lastChild || el);

        // 如果剩余内容为空，说明光标实际上是在末尾
        const remainingText = tempRange.toString().trim();
        return remainingText === '';
    }

    return true;
};

/**
 * 检查光标是否向前
 *
 * @param selection - 选区
 * @returns 是否向前
 */
export const checkIsForward = (selection: Selection) => {
    const anchorNode = selection.anchorNode as Node;
    const focusNode = selection.focusNode as Node;
    const anchorOffset = selection.anchorOffset;
    const focusOffset = selection.focusOffset;

    if (anchorNode.isSameNode(focusNode)) {
        return anchorOffset < focusOffset;
    }
    return anchorNode.compareDocumentPosition(focusNode) === Node.DOCUMENT_POSITION_FOLLOWING;
};

/**
 * 在当前光标位置插入文本
 *
 * @param text - 要插入的文本
 * @returns 是否插入成功
 */
export const insertTextAtCursor = (text: string): boolean => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);
    const container = range.startContainer;
    const offset = range.startOffset;

    if (container.nodeType === Node.TEXT_NODE) {
        // 如果是文本节点，直接在当前位置插入文本
        const currentText = container.textContent || '';
        container.textContent = currentText.slice(0, offset) + text + currentText.slice(offset);

        // 设置光标位置到插入文本后
        range.setStart(container, offset + text.length);
        range.setEnd(container, offset + text.length);
    } else {
        // 如果不是文本节点，创建新的文本节点
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // 设置光标位置到新节点后
        range.setStart(textNode, text.length);
        range.setEnd(textNode, text.length);
    }

    selection.removeAllRanges();
    selection.addRange(range);

    return true;
};

/**
 * 删除指定范围的文本内容
 *
 * @param node - 文本节点
 * @param start - 开始位置
 * @param end - 结束位置
 */
export const deleteTextRange = (node: Node, start: number, end: number): void => {
    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, end);

    selection.removeAllRanges();
    selection.addRange(range);

    // range.deleteContents(); // HACK: 暂时先试用 execCommand api，详见 README
    document.execCommand('delete', false);
};

type CaretPosition = {
    offsetNode: Node;
    offset: number;
};

/**
 * 获取脱字符号位置
 *
 * @see https://developer.mozilla.org/zh-CN/docs/Web/API/Document/caretPositionFromPoint
 * @see https://developer.mozilla.org/zh-CN/docs/Web/API/Document/caretRangeFromPoint
 * @param x - 水平坐标
 * @param y - 垂直坐标
 */
export const getCaretPosition = (x: number, y: number) => {
    if ((document as any).caretPositionFromPoint) {
        return (document as any).caretPositionFromPoint(x, y) as CaretPosition;
    }

    if (document.caretRangeFromPoint) {
        return document.caretRangeFromPoint(x, y) as Range;
    }

    return null;
};

/**
 * 根据点击坐标设置光标位置
 *
 * @param container - 容器元素
 * @param point - 点击坐标，包含 x 和 y
 */
export const setCaretPosition = (container: HTMLDivElement | null, point: { x: number; y: number }) => {
    if (!container) return;

    const { x, y } = point;

    const caretPosition = getCaretPosition(x, y);
    if (!caretPosition) return;

    const range = document.createRange();
    const selection = window.getSelection();

    // 清空选区
    if (selection) selection.removeAllRanges();

    // 根据返回类型判断如何获取节点和偏移量
    if ('startContainer' in caretPosition) {
        // caretPosition -> Range
        range.setStart(caretPosition.startContainer, caretPosition.startOffset);
    } else {
        // caretPosition -> CaretPosition
        range.setStart(caretPosition.offsetNode, caretPosition.offset);
    }

    range.collapse(true);

    if (selection) selection.addRange(range);
};

/**
 * 设置光标到容器末尾
 *
 * @param container - 容器元素
 */
export const setCaretToEnd = (container: HTMLDivElement | null) => {
    if (!container) return;

    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(false);

    const selection = window.getSelection();
    if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
    }
};
